import { Formik } from "formik"
import React from "react"
import DynamicItemForm from "./DynamicItemForm"
import { createCategoryItemsAction, editCategoryItemsAction } from "redux/hotel/action"
import { useDispatch, useSelector } from "react-redux"
import * as Yup from "yup"
import { capitalize, getDidYouKnowPayload, getHistoryPayload } from "helpers/functions"
import { hotelAction } from "redux/hotel/slice/user"
import FieldsModal from "../dynamic-forms/components/FieldsModal"
import { did_you_know_initial_values, history_initial_values } from "helpers/constant"

const AddItems = ({
  open = false,
  onClose = () => {},
  isEdit = false,
  dynamicFieldsGroup,
  linkNestedCategory
}) => {
  const dispatch = useDispatch()
  const { nestedSubcategoryData, hotelService, categoryItemDetails, imageIds, categoryImageId } =
    useSelector((state) => state.hotel)
  const { didYouKnowImage, soundbites } = useSelector((store) => store?.marketing ?? {})

  function generateInitialValues() {
    let initialValues = {}

    if (isEdit) {
      initialValues = {
        common: {
          name: categoryItemDetails?.name || "",
          images:
            categoryItemDetails?.images && categoryItemDetails?.images?.length
              ? categoryItemDetails?.images[0]
              : "",
          price: categoryItemDetails?.price || "",
          description: categoryItemDetails?.description || ""
        },
        did_you_know: isEdit
          ? categoryItemDetails?.did_you_know?.length > 0
            ? [
                ...categoryItemDetails.did_you_know.map((item) => ({
                  heading: item?.title ?? "",
                  content: item?.description ?? "",
                  image: item?.image ?? ""
                }))
              ]
            : [did_you_know_initial_values]
          : [did_you_know_initial_values],
        item_history: isEdit
          ? categoryItemDetails?.item_history
            ? {
                heading: categoryItemDetails?.item_history?.title ?? "",
                content: categoryItemDetails?.item_history?.description ?? ""
              }
            : history_initial_values
          : history_initial_values,
        soundbite: isEdit ? categoryItemDetails?.soundbite_info : "",
        soundbite_url: isEdit ? categoryItemDetails?.soundbite_url : "",
        soundbite_id: isEdit ? categoryItemDetails?.soundbite : "",
        soundbite_voice_id: isEdit ? categoryItemDetails?.soundbite_voice_id : ""
      }
      categoryItemDetails?.dynamic_fields?.forEach((section) => {
        const sectionKey = section.key
        initialValues[sectionKey] = {}

        if (Array.isArray(section?.value)) {
          section?.value?.forEach((field) => {
            const fieldType = field?.type?.toLowerCase()
            const fieldKey = field?.key

            if (fieldType === "image") {
              initialValues[sectionKey][fieldKey] = Array.isArray(field?.value)
                ? field?.value[0]
                : ""
            } else if (fieldType === "file" && Array.isArray(field?.value) && field?.value.length) {
              initialValues[sectionKey][fieldKey] = field?.value[0] || ""
            } else {
              initialValues[sectionKey][fieldKey] = field?.value || ""
            }
          })
        }
      })
    } else {
      dynamicFieldsGroup?.forEach((section) => {
        const sectionKey = section?.key
        initialValues[sectionKey] = {}
        section.fields.forEach((field) => {
          const fieldKey = field?.key
          initialValues[sectionKey][fieldKey] = ""
        })
      })
      initialValues = {
        ...initialValues,
        did_you_know: [did_you_know_initial_values],
        item_history: history_initial_values,
        soundbite: "",
        soundbite_id: "",
        soundbite_voice_id: ""
      }
    }

    return initialValues
  }

  const handleSubmitForm = (values) => {
    const dynamic_fields = []
    const extraFields = {}

    dynamicFieldsGroup?.forEach((section) => {
      const sectionKey = section?.key

      const sectionFieldValues = section?.fields?.map((field) => {
        const fieldType = field?.type?.toLowerCase()
        const formValue = values?.[sectionKey]?.[field.key] ?? ""
        const imageIdValue = imageIds?.[sectionKey]?.[field.key]
        let value = ""

        const useImageIds = Array.isArray(imageIdValue)

        if (fieldType === "image") {
          if (useImageIds) {
            value = imageIdValue
          } else {
            value = formValue?.id ? [formValue.id] : ""
          }
        } else if (fieldType === "file") {
          value = categoryImageId ? [categoryImageId] : [formValue?.id] || ""
        } else {
          value = formValue ?? ""
        }

        if (sectionKey === "common") {
          if (fieldType === "image") {
            if (useImageIds) {
              value = imageIdValue
            } else {
              value = formValue?.image_id ? [formValue?.image_id] : ""
            }
          }
          extraFields[field.key] = value
        }

        return {
          ...field,
          value
        }
      })

      if (sectionKey !== "common") {
        dynamic_fields.push({
          key: section.key,
          type: section.type,
          label: section.label,
          show_listing: section.show_listing,
          value: sectionFieldValues
        })
      }
    })

    const payload = {
      hotel_config: hotelService?.id,
      nested_nested_subcategory: nestedSubcategoryData?.nested_nested_subcategory_id,
      dynamic_fields,
      ...extraFields,
      did_you_know: getDidYouKnowPayload(values.did_you_know, didYouKnowImage),
      item_history: getHistoryPayload(values.item_history),
      soundbite:
        values?.soundbite !== "" ? (soundbites?.id ? soundbites?.id : values?.soundbite_id) : "",
      soundbite_info: values?.soundbite || "",
      voice_id: values?.soundbite_voice_id || ""
    }

    if (!isEdit) {
      dispatch(
        createCategoryItemsAction({ payload, id: linkNestedCategory?.id, onClose: handleClose })
      )
    } else {
      dispatch(
        editCategoryItemsAction({ payload, id: linkNestedCategory?.id, onClose: handleClose })
      )
    }

    dispatch(hotelAction.resetImageIds())
  }

  const handleClose = () => {
    onClose()
  }

  const createDynamicValidationSchema = (dynamicFieldsGroup = []) => {
    const shape = {}

    dynamicFieldsGroup?.forEach((section) => {
      const sectionShape = {}

      section.fields.forEach((field) => {
        const isRequired = section.key === "common" && field.key !== "images"

        let validator
        switch (field.type) {
          case "text":
          case "time":
            validator = Yup.string()
            break
          case "number":
            validator = Yup.number()
            break
          case "image":
          case "file":
          case "images":
            validator = Yup.mixed()
            break
          case "date":
            validator = Yup.date()
            break
          default:
            validator = Yup.mixed()
        }

        if (field.key === "name") {
          sectionShape[field.key] = Yup.string()
            .required(`${field.label} is required`)
            .max(40, "Max 40 characters allowed")
          return
        } else if (field.key === "price") {
          sectionShape[field.key] = Yup.string()
          return
        } else {
          sectionShape[field.key] = isRequired
            ? validator.required(`${capitalize(field.label)} is required`)
            : validator.nullable()
        }
      })

      shape[section.key] = Yup.object().shape(sectionShape)
    })

    return Yup.object().shape(shape)
  }

  return linkNestedCategory?.hotel_config_name?.includes("Restaurant") ? (
    <>
      <FieldsModal
        open={open}
        isEdit={isEdit}
        onClose={handleClose}
        dynamicFieldsGroup={dynamicFieldsGroup}
        title={categoryItemDetails?.name}
      />
    </>
  ) : (
    <Formik
      initialValues={generateInitialValues()}
      enableReinitialize
      validationSchema={createDynamicValidationSchema(dynamicFieldsGroup)}
      onSubmit={handleSubmitForm}>
      {(formik) => (
        <>
          <DynamicItemForm
            open={open}
            onClose={handleClose}
            isEdit={isEdit}
            formik={formik}
            subTitle={"Add New Sub Category"}
            dynamicFieldsGroup={dynamicFieldsGroup}
            title={categoryItemDetails?.name}
          />
        </>
      )}
    </Formik>
  )
}

export default AddItems
